import { QueryClient } from "@tanstack/react-query";
import { PostHog } from "posthog-js";
import * as React from "react";
import { Outlet, redirect, RouteObject } from "react-router";
import {
  ASSIGNMENT_PATHS,
  COMPANIES_PATHS,
  CONTENT_LIBRARY_PATHS,
  INTERVIEW_PATHS,
  JOB_PATHS,
  SURVEYS_PATHS,
} from "~/participant/constants/paths.constants";
import { UTAH_PATHS, UTAH_PROGRAM_AVAILABLE_STEPS } from "~/participant/constants/utah.constants";
import { maybeAccessStep, maybeRedirectToLatestStep } from "~/participant/utils/utah.helpers";
import { assignmentsListViewLoader } from "~/participant/views/AssignmentsListView.loader";
import { assignmentSubmissionViewLoader } from "~/participant/views/AssignmentSubmissionView.loader";
import { assignmentViewLoader } from "~/participant/views/AssignmentView/AssignmentView.loader";
import { companyLoader } from "~/participant/views/CompanyView.loader";
import { eventViewLoader } from "~/participant/views/EventView.loader";
import { gamePlansViewLoader } from "~/participant/views/GamePlansView.loader";
import { homeViewLoader } from "~/participant/views/HomeView/HomeView.loader";
import { jobGamePlansViewLoader } from "~/participant/views/JobGamePlansView/JobGamePlansView.loader";
import { jobViewLoader } from "~/participant/views/JobView/JobView.loader";
import { onboardingLoader } from "~/participant/views/OnboardingView/OnboardingView.loader";
import { participantSurveysViewLoader } from "~/participant/views/ParticipantSurveysView.loader";
import { savedViewLoader } from "~/participant/views/SavedView.loader";
import { surveyDetailViewLoader } from "~/participant/views/SurveyDetailView/SurveyDetailView.loader";
import { submissionViewLoader } from "~/participant/views/SurveySubmissionView.loader";
import { utahOnboardingSelfIdentificationLoader } from "~/participant/views/utah-onboarding/UtahOnboardingSelfIdentificationView.loader";
import { utahOnboardingTaxonomyLoader } from "~/participant/views/utah-onboarding/UtahOnboardingTaxonomyView.loader";
import { utahOnboardingLoader } from "~/participant/views/utah-onboarding/UtahOnboardingView.loader";
import { AssignmentNotFound } from "~/shared/components/AssignmentNotFound";
import { AssignmentSubmissionNotFound } from "~/shared/components/AssignmentSubmissionNotFound";
import { ContentLibraryNotFound } from "~/shared/components/ContentLibraryNotFound";
import { ButtonLink } from "~/shared/components/ds/ButtonLink";
import { LoadingContainer } from "~/shared/components/ds/Spinner";
import {
  EntityErrorHandler,
  NestedRouteErrorHandler,
  TopLevelRouteErrorHandler,
} from "~/shared/components/errors/Error";
import { NotFound } from "~/shared/components/errors/NotFound";
import { GamePlanNotFound } from "~/shared/components/GamePlanNotFound";
import { InterviewNotFound } from "~/shared/components/InterviewNotFound";
import { JobNotFound } from "~/shared/components/JobNotFound";
import { PrivateAppLayout } from "~/shared/components/PrivateAppLayout/PrivateAppLayout";
import { SurveyNotFound } from "~/shared/components/SurveyNotFound";
import { SurveySubmissionNotFound } from "~/shared/components/SurveySubmissionNotFound";
import { onboardedGuardLoader } from "~/shared/guards/onboarded.guard";
import { lazyRetry } from "~/shared/util/lazyRetry";
import { notFound } from "~/shared/util/notFound";
import { accountPreferencesViewLoader } from "~/shared/views/AccountPreferences/AccountPreferencesView.loader";
import { accountSkillsViewLoader } from "./views/AccountSkillsView/AccountSkillsView.loader";
import { CompanyNotFound } from "./views/CompanyNotFound";
import { interviewLoader } from "./views/InterviewView/InterviewView.loader";

export function getParticipantRoutes(queryClient: QueryClient, posthogClient?: PostHog): Array<RouteObject> {
  return [
    {
      loader: () => {
        return onboardedGuardLoader(queryClient);
      },
      children: [
        {
          errorElement: <TopLevelRouteErrorHandler />,
          element: (
            <PrivateAppLayout>
              <React.Suspense fallback={<LoadingContainer level="page" />}>
                <Outlet />
              </React.Suspense>
            </PrivateAppLayout>
          ),
          children: [
            {
              index: true,
              errorElement: <TopLevelRouteErrorHandler />,
              loader: async () => {
                return homeViewLoader(queryClient);
              },
              lazy: async () => {
                const { HomeView } = await lazyRetry(() => import("~/participant/views/HomeView/HomeView"), "HomeView");
                return {
                  Component: HomeView,
                };
              },
            },
            {
              path: "notifications",
              errorElement: <TopLevelRouteErrorHandler />,
              lazy: async () => {
                const { NotificationsView } = await lazyRetry(
                  () => import("~/participant/views/NotificationsView"),
                  "NotificationsView",
                );
                return {
                  Component: NotificationsView,
                };
              },
              children: [
                {
                  index: true,
                  errorElement: <NestedRouteErrorHandler />,
                  lazy: async () => {
                    const { NotificationsListView } = await lazyRetry(
                      () => import("~/shared/views/NotificationsListView/NotificationsListView"),
                      "NotificationsListView",
                    );
                    return {
                      Component: NotificationsListView,
                    };
                  },
                },
              ],
            },
            {
              path: "jobs",
              errorElement: <TopLevelRouteErrorHandler />,
              lazy: async () => {
                const { JobsView } = await lazyRetry(() => import("~/participant/views/JobsView"), "JobsView");
                return {
                  Component: JobsView,
                };
              },
            },
            {
              path: "jobs/:jobId",
              errorElement: (
                <EntityErrorHandler
                  renderNotFound={() => (
                    <JobNotFound>
                      <ButtonLink variant="default" to={JOB_PATHS.root}>
                        View Roles
                      </ButtonLink>
                    </JobNotFound>
                  )}
                />
              ),
              loader: async (args) => {
                return jobViewLoader(queryClient, posthogClient);
              },
              lazy: async () => {
                const { JobView } = await lazyRetry(() => import("~/participant/views/JobView/JobView"), "JobView");
                return {
                  Component: JobView,
                };
              },
            },
            // SAVED
            {
              path: "saved",
              errorElement: <TopLevelRouteErrorHandler />,
              loader: async (args) => {
                return savedViewLoader(args, queryClient, posthogClient);
              },
              lazy: async () => {
                const { SavedView } = await lazyRetry(() => import("~/participant/views/SavedView"), "SavedView");

                return {
                  Component: SavedView,
                };
              },
            },
            {
              path: "account",
              errorElement: <TopLevelRouteErrorHandler />,
              lazy: async () => {
                const { AccountView } = await lazyRetry(() => import("~/participant/views/AccountView"), "AccountView");
                return {
                  element: <AccountView />,
                };
              },
              children: [
                {
                  index: true,
                  loader: () => {
                    return accountPreferencesViewLoader(queryClient);
                  },
                  errorElement: <NestedRouteErrorHandler />,
                  lazy: async () => {
                    const { AccountPreferencesView } = await lazyRetry(
                      () => import("~/shared/views/AccountPreferences/AccountPreferencesView"),
                      "AccountPreferencesView",
                    );
                    return {
                      element: <AccountPreferencesView />,
                    };
                  },
                },
                {
                  path: "notifications",
                  errorElement: <NestedRouteErrorHandler />,
                  lazy: async () => {
                    const { AccountNotificationsPreferencesView } = await lazyRetry(
                      () => import("~/shared/views/AccountNotificationsPreferencesView"),
                      "AccountNotificationsPreferencesView",
                    );
                    return {
                      element: <AccountNotificationsPreferencesView />,
                    };
                  },
                },
                {
                  path: "skills",
                  loader: async () => {
                    return accountSkillsViewLoader(queryClient);
                  },
                  errorElement: <NestedRouteErrorHandler />,
                  lazy: async () => {
                    const { AccountSkillsView } = await lazyRetry(
                      () => import("~/participant/views/AccountSkillsView/AccountSkillsView"),
                      "AccountSkillsView",
                    );
                    return {
                      Component: AccountSkillsView,
                    };
                  },
                },
              ],
            },
            // INTERVIEWS
            {
              path: "interviews",
              errorElement: <TopLevelRouteErrorHandler />,
              lazy: async () => {
                const { InterviewsView } = await lazyRetry(
                  () => import("~/participant/views/InterviewsView/InterviewsView"),
                  "InterviewsView",
                );
                return {
                  Component: InterviewsView,
                };
              },
            },
            {
              path: "interviews/:interviewId",
              loader: async (args) => {
                return interviewLoader(args, queryClient);
              },
              errorElement: (
                <EntityErrorHandler
                  renderNotFound={() => (
                    <InterviewNotFound>
                      <ButtonLink to={INTERVIEW_PATHS.root}>View Interviews</ButtonLink>
                    </InterviewNotFound>
                  )}
                />
              ),
              lazy: async () => {
                const { InterviewView } = await lazyRetry(
                  () => import("~/participant/views/InterviewView/InterviewView"),
                  "InterviewView",
                );
                return {
                  Component: InterviewView,
                };
              },
            },
            {
              path: "interviews/create",
              errorElement: <TopLevelRouteErrorHandler />,
              lazy: async () => {
                const { CreateInterviewView } = await lazyRetry(
                  () => import("~/participant/views/CreateInterviewView"),
                  "CreateInterviewView",
                );
                return {
                  Component: CreateInterviewView,
                };
              },
            },
            // EVENTS
            {
              path: "events",
              errorElement: <TopLevelRouteErrorHandler />,
              lazy: async () => {
                const { EventsView } = await lazyRetry(() => import("~/participant/views/EventsView"), "EventsView");
                return {
                  Component: EventsView,
                };
              },
              children: [
                {
                  index: true,
                  errorElement: <NestedRouteErrorHandler />,
                  lazy: async () => {
                    const { EventTypeView } = await lazyRetry(
                      () => import("~/shared/views/EventTypeView"),
                      "EventTypeView",
                    );
                    return {
                      Component: EventTypeView,
                    };
                  },
                },
                {
                  path: "type/:eventTypeId",
                  errorElement: <NestedRouteErrorHandler />,
                  lazy: async () => {
                    const { EventTypeView } = await lazyRetry(
                      () => import("~/shared/views/EventTypeView"),
                      "EventTypeView",
                    );
                    return {
                      Component: EventTypeView,
                    };
                  },
                },
                {
                  path: "type/all",
                  loader: () => {
                    return redirect("/events");
                  },
                },
              ],
            },
            {
              path: "events/:eventId",
              errorElement: <TopLevelRouteErrorHandler />,
              shouldRevalidate: () => false,
              loader: async (args) => {
                return eventViewLoader(args, queryClient);
              },
              lazy: async () => {
                const { EventView } = await lazyRetry(() => import("~/participant/views/EventView"), "EventView");
                return {
                  Component: EventView,
                };
              },
            },
            // ASSIGNMENTS
            {
              path: "assignments",
              errorElement: <TopLevelRouteErrorHandler />,
              lazy: async () => {
                const { AssignmentsView } = await lazyRetry(
                  () => import("~/participant/views/AssignmentsView"),
                  "AssignmentsView",
                );
                return {
                  Component: AssignmentsView,
                };
              },
              children: [
                {
                  index: true,
                  errorElement: <NestedRouteErrorHandler />,
                  shouldRevalidate: () => false,
                  loader: async () => {
                    return assignmentsListViewLoader(queryClient);
                  },
                  lazy: async () => {
                    const { AssignmentsListView } = await lazyRetry(
                      () => import("~/participant/views/AssignmentsListView"),
                      "AssignmentsListView",
                    );
                    return {
                      Component: AssignmentsListView,
                    };
                  },
                },
                {
                  path: "pending",
                  errorElement: <NestedRouteErrorHandler />,
                  shouldRevalidate: () => false,
                  loader: () => {
                    return assignmentsListViewLoader(queryClient, "pending");
                  },
                  lazy: async () => {
                    const { AssignmentsPendingListView } = await lazyRetry(
                      () => import("~/participant/views/AssignmentsListView"),
                      "AssignmentsPendingListView",
                    );
                    return {
                      element: <AssignmentsPendingListView type="pending" />,
                    };
                  },
                },
                {
                  path: "submitted",
                  errorElement: <NestedRouteErrorHandler />,
                  shouldRevalidate: () => false,
                  loader: async () => {
                    return assignmentsListViewLoader(queryClient, "submitted");
                  },
                  lazy: async () => {
                    const { AssignmentsSubmittedListView } = await lazyRetry(
                      () => import("~/participant/views/AssignmentsListView"),
                      "AssignmentsSubmittedListView",
                    );
                    return {
                      element: <AssignmentsSubmittedListView type="submitted" />,
                    };
                  },
                },
              ],
            },
            {
              path: "assignments/submissions",
              loader: () => redirect(ASSIGNMENT_PATHS.root),
            },
            {
              path: "assignments/:assignmentId",
              errorElement: (
                <EntityErrorHandler
                  renderNotFound={() => (
                    <AssignmentNotFound>
                      <ButtonLink variant="default" to={ASSIGNMENT_PATHS.root}>
                        View Assignments
                      </ButtonLink>
                    </AssignmentNotFound>
                  )}
                />
              ),
              shouldRevalidate: () => false,
              loader: async (args) => {
                return assignmentViewLoader(args, queryClient);
              },
              lazy: async () => {
                const { AssignmentView } = await lazyRetry(
                  () => import("~/participant/views/AssignmentView/AssignmentView"),
                  "AssignmentView",
                );
                return {
                  Component: AssignmentView,
                };
              },
            },
            {
              path: "assignments/submissions/:submissionId",
              errorElement: (
                <EntityErrorHandler
                  renderNotFound={() => (
                    <AssignmentSubmissionNotFound>
                      <ButtonLink to={ASSIGNMENT_PATHS.root}>View Assignments</ButtonLink>
                    </AssignmentSubmissionNotFound>
                  )}
                />
              ),
              shouldRevalidate: () => false,
              loader: async (args) => {
                return assignmentSubmissionViewLoader(args, queryClient);
              },
              lazy: async () => {
                const { AssignmentSubmissionView } = await lazyRetry(
                  () => import("~/participant/views/AssignmentSubmissionView"),
                  "AssignmentSubmissionView",
                );
                return {
                  Component: AssignmentSubmissionView,
                };
              },
            },
            {
              path: "surveys/:surveyId",
              errorElement: (
                <EntityErrorHandler
                  renderNotFound={() => (
                    <SurveyNotFound>
                      <ButtonLink variant="default" to={SURVEYS_PATHS.reflections}>
                        View Surveys
                      </ButtonLink>
                    </SurveyNotFound>
                  )}
                />
              ),
              shouldRevalidate: () => false,
              loader: async (args) => {
                return surveyDetailViewLoader(args, queryClient);
              },
              lazy: async () => {
                const { SurveyDetailView } = await lazyRetry(
                  () => import("~/participant/views/SurveyDetailView/SurveyDetailView"),
                  "SurveyDetailView",
                );
                return {
                  element: <SurveyDetailView />,
                };
              },
            },
            {
              path: "surveys",
              loader: () => {
                return redirect(SURVEYS_PATHS.reflections);
              },
            },
            {
              path: "surveys/submissions",
              loader: () => {
                return redirect(SURVEYS_PATHS.reflections);
              },
            },
            {
              path: "surveys/submissions/:submissionId",
              errorElement: (
                <EntityErrorHandler
                  renderNotFound={() => (
                    <SurveySubmissionNotFound>
                      <ButtonLink to={SURVEYS_PATHS.reflections}>View surveys</ButtonLink>
                    </SurveySubmissionNotFound>
                  )}
                />
              ),
              shouldRevalidate: () => false,
              loader: async (args) => {
                return submissionViewLoader(args, queryClient);
              },
              lazy: async () => {
                const { SubmissionView } = await lazyRetry(
                  () => import("~/participant/views/SurveySubmissionView"),
                  "SubmissionView",
                );
                return {
                  Component: SubmissionView,
                };
              },
            },
            {
              path: "interview-reflections",
              errorElement: <TopLevelRouteErrorHandler />,
              shouldRevalidate: () => false,
              loader: async () => {
                return participantSurveysViewLoader(queryClient);
              },
              lazy: async () => {
                const { ParticipantSurveysView } = await lazyRetry(
                  () => import("~/participant/views/ParticipantSurveysView"),
                  "ParticipantSurveysView",
                );
                return {
                  Component: ParticipantSurveysView,
                };
              },
            },
            {
              path: "companies",
              errorElement: <TopLevelRouteErrorHandler />,
              lazy: async () => {
                const { CompaniesView } = await lazyRetry(
                  () => import("~/participant/views/CompaniesView/CompaniesView"),
                  "CompaniesView",
                );
                return {
                  Component: CompaniesView,
                };
              },
            },
            {
              path: "companies/:partnerId",
              errorElement: <EntityErrorHandler renderNotFound={() => <CompanyNotFound />} />,
              loader: async (args) => {
                return companyLoader(args, queryClient);
              },
              lazy: async () => {
                const { FeatureFlaggedCompanyView } = await lazyRetry(
                  () => import("~/participant/views/CompanyView/FeatureFlaggedCompanyView"),
                  "CompanyView",
                );
                return {
                  Component: FeatureFlaggedCompanyView,
                };
              },
            },
            {
              path: "partners",
              loader: async () => {
                return redirect(COMPANIES_PATHS.companies);
              },
              children: [
                {
                  path: ":partnerId",
                  loader: async ({ params }) => {
                    if (!params.partnerId) {
                      return notFound();
                    }

                    return redirect(COMPANIES_PATHS.detail(params.partnerId));
                  },
                  children: [
                    {
                      path: "preview",
                      loader: async ({ params }) => {
                        if (!params.partnerId) {
                          return notFound();
                        }

                        return redirect(COMPANIES_PATHS.detail(params.partnerId));
                      },
                    },
                  ],
                },
              ],
            },
            // GAME PLANS
            {
              path: "jobs/game-plan/:gamePlanId",
              errorElement: (
                <EntityErrorHandler
                  renderNotFound={() => (
                    <GamePlanNotFound>
                      <ButtonLink to={JOB_PATHS.gamePlan()}>View Game Plans</ButtonLink>
                    </GamePlanNotFound>
                  )}
                />
              ),
              loader: async (args) => {
                return jobGamePlansViewLoader(args, queryClient);
              },
              lazy: async () => {
                const { JobGamePlansView } = await lazyRetry(
                  () => import("~/participant/views/JobGamePlansView/JobGamePlansView"),
                  "JobGamePlansView",
                );
                return {
                  Component: JobGamePlansView,
                };
              },
            },
            {
              path: "game-plans",
              errorElement: <TopLevelRouteErrorHandler />,
              loader: async () => {
                return gamePlansViewLoader(queryClient);
              },
              lazy: async () => {
                const { GamePlansView } = await lazyRetry(
                  () => import("~/participant/views/GamePlansView"),
                  "GamePlansView",
                );
                return {
                  Component: GamePlansView,
                };
              },
            },
            // CONTENT LIBRARY
            {
              path: "content-library",
              errorElement: <TopLevelRouteErrorHandler />,
              lazy: async () => {
                const { ContentLibraryView } = await lazyRetry(
                  () => import("~/participant/views/ContentLibraryView"),
                  "ContentLibraryView",
                );
                return {
                  Component: ContentLibraryView,
                };
              },
            },
            {
              path: "content-library/:contentId",
              errorElement: (
                <EntityErrorHandler
                  renderNotFound={() => (
                    <ContentLibraryNotFound>
                      <ButtonLink variant="default" to={CONTENT_LIBRARY_PATHS.root}>
                        View Resources
                      </ButtonLink>
                    </ContentLibraryNotFound>
                  )}
                />
              ),
              lazy: async () => {
                const { ContentLibraryItemView } = await lazyRetry(
                  () => import("~/participant/views/ContentLibraryItemView"),
                  "ContentLibraryItemView",
                );
                return {
                  Component: ContentLibraryItemView,
                };
              },
            },
            {
              path: "*",
              element: (
                <div className="content-container flex min-h-[calc(100dvh-var(--siteheader-height))] items-center justify-center">
                  <NotFound />
                </div>
              ),
            },
          ],
        },
      ],
    },
    {
      path: "onboarding",
      errorElement: <TopLevelRouteErrorHandler />,
      loader: async () => {
        return onboardingLoader(queryClient);
      },
      lazy: async () => {
        const { OnboardingView } = await lazyRetry(
          () => import("~/participant/views/OnboardingView/OnboardingView"),
          "OnboardingView",
        );

        return {
          Component: OnboardingView,
        };
      },
    },
    {
      path: UTAH_PATHS.onboarding,
      errorElement: <TopLevelRouteErrorHandler />,
      shouldRevalidate: () => false,
      loader: async () => {
        return utahOnboardingLoader(queryClient, posthogClient);
      },
      lazy: async () => {
        const { UtahOnboardingView } = await lazyRetry(
          () => import("~/participant/views/utah-onboarding/UtahOnboardingView"),
          "UtahOnboardingView",
        );
        return {
          Component: UtahOnboardingView,
        };
      },
      children: [
        {
          index: true,
          errorElement: <NestedRouteErrorHandler />,
          loader: (args) => {
            return maybeRedirectToLatestStep(args.request.url, queryClient, () => {
              return maybeAccessStep(UTAH_PROGRAM_AVAILABLE_STEPS.welcome.step, queryClient, () => {
                return null;
              });
            });
          },
          lazy: async () => {
            const { UtahOnboardingWelcomeView } = await lazyRetry(
              () => import("~/participant/views/utah-onboarding/UtahOnboardingWelcomeView"),
              "UtahOnboardingWelcomeView",
            );
            return {
              Component: UtahOnboardingWelcomeView,
            };
          },
        },
        {
          path: "basic-info",
          errorElement: <NestedRouteErrorHandler />,
          lazy: async () => {
            const { UtahOnboardingBasicInfoView } = await lazyRetry(
              () => import("~/participant/views/utah-onboarding/UtahOnboardingBasicInfoView"),
              "UtahOnboardingBasicInfoView",
            );
            return {
              loader: (args) => {
                return maybeRedirectToLatestStep(args.request.url, queryClient, () => {
                  return maybeAccessStep(UTAH_PROGRAM_AVAILABLE_STEPS["basic-info"].step, queryClient, () => {
                    return null;
                  });
                });
              },
              Component: UtahOnboardingBasicInfoView,
            };
          },
        },
        {
          path: "self-identification",
          loader: (args) => {
            return maybeRedirectToLatestStep(args.request.url, queryClient, () => {
              return maybeAccessStep(UTAH_PROGRAM_AVAILABLE_STEPS.demographics.step, queryClient, () => {
                return utahOnboardingSelfIdentificationLoader(queryClient);
              });
            });
          },
          lazy: async () => {
            const { UtahOnboardingSelfIdentificationView } = await lazyRetry(
              () => import("~/participant/views/utah-onboarding/UtahOnboardingSelfIdentificationView"),
              "UtahOnboardingSelfIdentificationView",
            );
            return {
              element: <UtahOnboardingSelfIdentificationView />,
            };
          },
        },
        {
          path: "taxonomy",
          loader: (args) => {
            return maybeRedirectToLatestStep(args.request.url, queryClient, () => {
              return maybeAccessStep(UTAH_PROGRAM_AVAILABLE_STEPS.taxonomy.step, queryClient, () => {
                return utahOnboardingTaxonomyLoader(queryClient);
              });
            });
          },
          lazy: async () => {
            const { UtahOnboardingTaxonomyView } = await lazyRetry(
              () => import("~/participant/views/utah-onboarding/UtahOnboardingTaxonomyView"),
              "UtahOnboardingTaxonomyView",
            );
            return {
              element: <UtahOnboardingTaxonomyView />,
            };
          },
        },
        {
          path: "bonus",
          loader: (args) => {
            return maybeRedirectToLatestStep(args.request.url, queryClient, () => {
              return maybeAccessStep(UTAH_PROGRAM_AVAILABLE_STEPS.bonus.step, queryClient, () => {
                return null;
              });
            });
          },
          lazy: async () => {
            const { UtahOnboardingBonusView } = await lazyRetry(
              () => import("~/participant/views/utah-onboarding/UtahOnboardingBonusView"),
              "UtahOnboardingBonusView",
            );
            return {
              element: <UtahOnboardingBonusView />,
            };
          },
        },
        {
          path: "commitment",
          loader: (args) => {
            return maybeRedirectToLatestStep(args.request.url, queryClient, () => {
              return maybeAccessStep(UTAH_PROGRAM_AVAILABLE_STEPS.commitment.step, queryClient, () => {
                return null;
              });
            });
          },
          lazy: async () => {
            const { UtahOnboardingCommitmentView } = await lazyRetry(
              () => import("~/participant/views/utah-onboarding/UtahOnboardingCommitmentView"),
              "UtahOnboardingCommitmentView",
            );
            return {
              element: <UtahOnboardingCommitmentView />,
            };
          },
        },
        {
          path: "*",
          element: (
            <div className="content-container flex min-h-[calc(100dvh-var(--siteheader-height))] items-center justify-center">
              <NotFound />
            </div>
          ),
        },
      ],
    },
  ];
}
